import { useState } from 'react'
import { useParams } from 'react-router'
import { EyeOff, Info, Lock, Plus } from 'lucide-react'
import { EmptyState, FormDialog, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import {
  useEngagementMutations, useSurvey, useSurveyQuestions, useSurveyResults,
} from '../api'
import { ANONYMITY_LABELS, QUESTION_TYPE_LABELS, SURVEY_STATUS_LABELS } from '../types'
import type { QuestionType, SurveyAnonymity, SurveyStatus } from '../types'

/** One survey: its questions, and what its answers are allowed to say. */
export function SurveyResultsPage() {
  useBreadcrumb()
  const { id } = useParams<{ id: string }>()
  const survey = useSurvey(id)
  const questions = useSurveyQuestions(id)
  const results = useSurveyResults(id)
  const { addQuestion, publishSurvey } = useEngagementMutations()

  const [draft, setDraft] = useState<{
    text: string; questionType: QuestionType; options: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dialogError, setDialogError] = useState<string | null>(null)

  if (survey.isPending) {
    return <Skeleton className="h-64" />
  }
  if (!survey.isSuccess) {
    return <EmptyState title="That survey could not be loaded" description="It may have been removed." />
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={survey.data.title}
        subtitle={`${survey.data.code} · ${ANONYMITY_LABELS[survey.data.anonymity as SurveyAnonymity]}`}
        actions={
          <div className="flex flex-wrap gap-2">
            {survey.data.status === 'DRAFT' && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setError(null)
                    setDraft({ text: '', questionType: 'SCALE_1_5', options: '' })
                  }}
                >
                  <Plus className="size-4" aria-hidden="true" />
                  Add a question
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    void (async () => {
                      setError(null)
                      try {
                        await publishSurvey.mutateAsync(survey.data.id)
                      } catch (caught) {
                        setError(toApiProblem(caught)?.detail
                          ?? 'That survey could not be published')
                      }
                    })()
                  }}
                >
                  Send it
                </Button>
              </>
            )}
            <StatusBadge
              status={survey.data.status}
              tone={survey.data.status === 'OPEN' ? 'info'
                : survey.data.status === 'CLOSED' ? 'success' : 'neutral'}
              label={SURVEY_STATUS_LABELS[survey.data.status as SurveyStatus] ?? survey.data.status}
            />
          </div>
        }
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <p className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        <EyeOff className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        {survey.data.anonymityNote}
      </p>

      <Card>
        <h2 className="border-b border-slate-100 p-4 font-semibold text-[var(--color-heading)]">Questions</h2>
        {questions.isSuccess && questions.data.length === 0 && (
          <EmptyState
            title="No question has been added"
            description="A survey with no questions asks nothing, and cannot be sent."
          />
        )}
        {questions.isSuccess && questions.data.length > 0 && (
          <ol className="divide-y divide-slate-100">
            {questions.data.map((question) => (
              <li key={question.id} className="flex items-start justify-between gap-4 p-4 text-sm">
                <span>{question.questionOrder}. {question.text}</span>
                <span className="shrink-0 text-xs text-slate-600">
                  {QUESTION_TYPE_LABELS[question.questionType as QuestionType]
                    ?? question.questionType}
                </span>
              </li>
            ))}
          </ol>
        )}
      </Card>

      <Card>
        <h2 className="border-b border-slate-100 p-4 font-semibold text-[var(--color-heading)]">Results</h2>
        {results.isPending && <Skeleton className="m-4 h-20" />}
        {results.isSuccess && (
          <div className="space-y-4 p-4 text-sm">
            <p className={`flex items-start gap-2 ${
              results.data.released ? 'text-slate-700' : 'text-amber-800'
            }`}>
              {results.data.released
                ? <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                : <Lock className="mt-0.5 size-4 shrink-0" aria-hidden="true" />}
              {results.data.note}
            </p>

            {results.data.enps && (
              <div className="rounded-md border border-slate-200 p-3">
                <h3 className="font-medium text-[var(--color-heading)]">eNPS</h3>
                <p className="text-2xl font-semibold">
                  {results.data.enps.score == null ? '—' : results.data.enps.score}
                </p>
                <p className="text-slate-600">{results.data.enps.note}</p>
              </div>
            )}

            <div className="space-y-3">
              {results.data.questions.map((question) => (
                <div key={question.questionId} className="rounded-md border border-slate-200 p-3">
                  <h3 className="font-medium text-[var(--color-heading)]">{question.text}</h3>
                  {question.average == null ? (
                    <p className="mt-1 text-amber-800">
                      {question.withheldReason ?? 'Nothing to show.'}
                    </p>
                  ) : (
                    <>
                      <p className="mt-1 text-lg font-semibold">
                        {question.average.toFixed(2)}
                        <span className="ms-2 text-xs font-normal text-slate-600">
                          from {question.responses} answer(s)
                        </span>
                      </p>
                      <p className="mt-1 text-xs text-slate-600">
                        {Object.entries(question.distribution)
                          .map(([value, count]) => `${value}: ${count}`)
                          .join(' · ')}
                      </p>
                    </>
                  )}
                </div>
              ))}
            </div>

            {results.data.byDepartment.length > 0 && (
              <div>
                <h3 className="font-medium text-[var(--color-heading)]">By department</h3>
                <ul className="mt-2 space-y-1 text-xs text-slate-600">
                  {results.data.byDepartment.map((group) => (
                    <li key={group.departmentId}>
                      {group.average == null
                        ? group.withheldReason
                        : `${group.responses} answer(s), average ${group.average.toFixed(2)}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setDialogError(null) } }}
        title="Add a question"
        description="Questions are fixed once the survey goes out, so two people cannot answer different surveys under one name."
        submitLabel="Add it"
        loading={addQuestion.isPending}
        error={dialogError}
        onSubmit={() => {
          if (!draft || !id) return
          void (async () => {
            setDialogError(null)
            try {
              await addQuestion.mutateAsync({
                id,
                body: {
                  text: draft.text,
                  questionType: draft.questionType,
                  options: draft.options
                    ? draft.options.split(',').map((option) => option.trim()).filter(Boolean)
                    : [],
                  required: true,
                },
              })
              setDraft(null)
            } catch (caught) {
              setDialogError(toApiProblem(caught)?.detail ?? 'That question could not be added')
            }
          })()
        }}
      >
        {draft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="question-text">The question</Label>
              <Input
                id="question-text" value={draft.text}
                onChange={(event) => setDraft({ ...draft, text: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="question-type">Answered as</Label>
              <Select
                id="question-type"
                className="w-full"
                value={draft.questionType}
                onChange={(event) =>
                  setDraft({ ...draft, questionType: event.target.value as QuestionType })}
              >
                {Object.entries(QUESTION_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-slate-600">
                An eNPS needs a 0-to-10 question; without one there is no score to compute.
              </p>
            </div>
            {draft.questionType === 'SINGLE_CHOICE' && (
              <div>
                <Label htmlFor="question-options">Choices</Label>
                <Input
                  id="question-options" value={draft.options}
                  onChange={(event) => setDraft({ ...draft, options: event.target.value })}
                />
                <p className="mt-1 text-xs text-slate-600">
                  Comma separated. At least two, or it is a text box pretending otherwise.
                </p>
              </div>
            )}
          </div>
        )}
      </FormDialog>
    </div>
  )
}
